import React, { useEffect, useState } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Container,
  Table,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
} from "reactstrap";
import { faFileCsv, faFileExcel } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { reportsApi } from "../../../services/reportsServices";
import moment from "moment";
import { useAuth } from "../../../providers/authProvider";
import InformationModal from "../../../components/InformationModal";
import Loader from "../../../components/Loader";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import PeriodTypeSelector from "../../../components/PeriodTypeSelector";
import WeekSelector from "../../../components/WeekSelector";
import CustomPeriodSelector from "../../../components/CustomPeriodSelector";

const TYPE_WEEKLY = "TYPE_WEEKLY";

const LocationTable = ({ location }) => {
  return (
    <div className="mb-8">
      <h3 className="mb-4">{location.locationName}</h3>
      <Table className="w-full">
        <thead>
          <tr className="bg-gray-100">
            <th className="border border-gray-400 p-2">Employee Name</th>
            <th className="border border-gray-400 p-2">Job Time</th>
            <th className="border border-gray-400 p-2">Idle Time</th>
            <th className="border border-gray-400 p-2">Warranty Time</th>
            <th className="border border-gray-400 p-2">Total</th>
            <th className="border border-gray-400 p-2">Hrs/Week</th>
            <th className="border border-gray-400 p-2">Rec %</th>
          </tr>
        </thead>
        <tbody>
          {location.employees.map((employee, index) => {
            const hrsWeek =
              employee.weekOrder > 0
                ? parseFloat((employee.total / employee.weekOrder).toFixed(2))
                : 0;
            const recPercentage =
              employee.total > 0
                ? Math.round((employee.job / employee.total) * 100)
                : 0;

            return (
              <tr key={index}>
                <td className="border border-gray-400 p-2">
                  {employee.employeeName}
                </td>
                <td className="border border-gray-400 p-2 text-right">
                  {employee.job}
                </td>
                <td className="border border-gray-400 p-2 text-right">
                  {employee.shop}
                </td>
                <td className="border border-gray-400 p-2 text-right">
                  {employee.warranty}
                </td>
                <td className="border border-gray-400 p-2 text-right">
                  {Number.isInteger(employee.total)
                    ? employee.total
                    : employee.total.toFixed(2)}
                </td>
                <td className="border border-gray-400 p-2 text-right">
                  {hrsWeek}
                </td>
                <td className="border border-gray-400 p-2 text-right">
                  {recPercentage}%
                </td>
              </tr>
            );
          })}
          <tr className="font-bold bg-gray-100">
            <td
              className="border border-gray-400 p-2"
              style={{ fontWeight: "bold" }}
            >
              Headcount {location.totals.headCount}
            </td>
            <td
              className="border border-gray-400 p-2 text-right"
              style={{ fontWeight: "bold" }}
            >
              {location.totals.jobTime}
            </td>
            <td
              className="border border-gray-400 p-2 text-right"
              style={{ fontWeight: "bold" }}
            >
              {location.totals.idleTime}
            </td>
            <td
              className="border border-gray-400 p-2 text-right"
              style={{ fontWeight: "bold" }}
            >
              {location.totals.warrantyTime}
            </td>
            <td
              className="border border-gray-400 p-2 text-right"
              style={{ fontWeight: "bold" }}
            >
              {location.totals.total}
            </td>
            <td
              className="border border-gray-400 p-2 text-right"
              style={{ fontWeight: "bold" }}
            >
              {location.totals.hrsWeek}
            </td>
            <td
              className="border border-gray-400 p-2 text-right"
              style={{ fontWeight: "bold" }}
            >
              {location.totals.total > 0
                ? Math.round(
                    (location.totals.jobTime / location.totals.total) * 100
                  )
                : 0}
              %
            </td>
          </tr>
        </tbody>
      </Table>
    </div>
  );
};

const RecoveryReport = () => {
  const [authContext] = useAuth();
  const [locationGroups, setLocationGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [monday, setMonday] = useState(moment().startOf("isoWeek"));
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [informationModal, setInformationModal] = useState({
    isOpen: false,
    title: "",
    body: "",
  });

  const [periodType, setPeriodType] = useState({
    value: TYPE_WEEKLY,
    label: "Weekly",
  });
  const [customStartDate, setCustomStartDate] = useState(
    moment().startOf("isoWeek")
  );
  const [customEndDate, setCustomEndDate] = useState(moment().endOf("isoWeek"));

  const toggleDownloadOptions = () => setDropdownOpen(!dropdownOpen);

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;

    const fetchData = async () => {
      setLoading(true);
      const condition = {
        serviceLocationId: authContext.serviceLocationId,
      };
      if (periodType.value === TYPE_WEEKLY) {
        condition.monthStart = moment(monday).format("YYYY-MM-DD");
        condition.endDate = moment(monday)
          .endOf("isoWeek")
          .format("YYYY-MM-DD");
      } else {
        condition.monthStart = moment(customStartDate).format("YYYY-MM-DD");
        condition.endDate = moment(customEndDate).format("YYYY-MM-DD");
      }
      try {
        const [recoveryReportResponse] = await Promise.all([
          reportsApi.recoveryReport(condition, { signal }),
        ]);
        setLocationGroups(recoveryReportResponse);
      } catch (err) {
        if (err.name !== "AbortError") {
          setInformationModal({
            isOpen: true,
            title: "Error",
            body:
              err?.response?.data?.[0]?.msg ||
              "There was an error with your request.",
          });
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    return () => controller.abort();
  }, [
    monday,
    periodType.value,
    authContext.serviceLocationId,
    customEndDate,
    customStartDate,
  ]);

  const formatDateRange = (startDate, endDate) => {
    return `${moment(startDate).format("MMMM Do, YYYY")} thru ${moment(
      endDate
    ).format("MMMM Do, YYYY")}`;
  };

  const onExport = async (format) => {
    const exportData = locationGroups.reduce((p, region) => {
      region.locations.forEach((location) => {
        location.employees.forEach((employee) => {
          const hrsWeek =
            employee.weekOrder > 0
              ? parseFloat((employee.total / employee.weekOrder).toFixed(2))
              : 0;
          const recPercentage =
            employee.total > 0
              ? Math.round((employee.job / employee.total) * 100)
              : 0;

          const item = {
            Region: region.regionName || "No Region",
            Location: location.locationName,
            "Employee Name": employee.employeeName,
            "Job Time": employee.job,
            "Idle Time": employee.shop,
            "Warranty Time": employee.warranty,
            Total: Number.isInteger(employee.total)
              ? employee.total
              : employee.total.toFixed(2),
            "Hrs/Week": hrsWeek,
            "Rec %": `${recPercentage}%`,
          };
          p.push(item);
        });

        const locationRecPercentage =
          location.totals.total > 0
            ? Math.round(
                (location.totals.jobTime / location.totals.total) * 100
              )
            : 0;

        p.push({
          Region: region.regionName || "No Region",
          Location: location.locationName,
          "Employee Name": `Head Count ${location.totals.headCount}`,
          "Job Time": location.totals.jobTime,
          "Idle Time": location.totals.idleTime,
          "Warranty Time": location.totals.warrantyTime,
          Total: location.totals.total,
          "Hrs/Week": location.totals.hrsWeek,
          "Rec %": `${locationRecPercentage}%`,
        });
      });
      return p;
    }, []);

    const filename = `Recovery-${
      periodType.value === TYPE_WEEKLY
        ? formatDateRange(monday, moment(monday).endOf("isoWeek"))
        : formatDateRange(customStartDate, customEndDate)
    }`.replace(/[\s,]/g, "-");

    if (format === "csv") {
      const CSVDownloadData = Papa.unparse(exportData, { delimiter: ";" });
      const hiddenElement = document.createElement("a");
      hiddenElement.href =
        "data:text/csv; charset=utf-8,\ufeff" +
        encodeURIComponent(CSVDownloadData);
      hiddenElement.target = "_self";
      hiddenElement.download = `${filename}.csv`;
      hiddenElement.click();
    } else if (format === "xlsx") {
      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Recovery Report");
      XLSX.writeFile(workbook, `${filename}.xlsx`);
    }
  };

  return (
    <Container fluid>
      <Card className="col-12 px-0">
        <CardHeader className="d-flex align-items-center justify-content-between mt-2">
          <div className="text-dark flex-grow-1 d-flex align-items-center">
            <h3 className="mb-0 ">Recovery Report</h3>
          </div>
          <div className="d-flex align-items-center">
            <div className="d-flex justify-content-between">
              <PeriodTypeSelector
                periodType={periodType}
                setPeriodType={setPeriodType}
              />
              {periodType.value === TYPE_WEEKLY ? (
                <WeekSelector
                  loading={loading}
                  monday={monday}
                  setMonday={setMonday}
                />
              ) : (
                <CustomPeriodSelector
                  defaultEndDate={customEndDate}
                  defaultStartDate={customStartDate}
                  onSubmit={(startDate, endDate) => {
                    setCustomStartDate(startDate);
                    setCustomEndDate(endDate);
                  }}
                />
              )}
            </div>
          </div>
          <Dropdown
            isOpen={dropdownOpen}
            toggle={toggleDownloadOptions}
            direction="end"
            size="sm"
            className="ml-2 rounded"
            disabled={loading || !locationGroups.length}
          >
            <DropdownToggle caret>Download Options</DropdownToggle>
            <DropdownMenu style={{ marginTop: 30 }}>
              <DropdownItem onClick={() => onExport("csv")}>
                <FontAwesomeIcon icon={faFileCsv} className="mr-2" />
                CSV File
              </DropdownItem>
              <DropdownItem onClick={() => onExport("xlsx")}>
                <FontAwesomeIcon icon={faFileExcel} className="mr-2" />
                XLS File
              </DropdownItem>
            </DropdownMenu>
          </Dropdown>
        </CardHeader>
        <CardBody className="d-flex flex-column">
          {loading ? (
            <Loader size="sm" />
          ) : (
            <div className="p-4">
              {locationGroups.length
                ? locationGroups.map((group) => (
                    <div
                      key={group.regionName}
                      style={{ marginBottom: "30px" }}
                    >
                      <div className="text-center mb-4">
                        <h3 className="font-bold">
                          Service Recovery {group.regionName || "No"} Region
                        </h3>
                        <p className="text-lg">
                          {periodType.value === TYPE_WEEKLY
                            ? formatDateRange(
                                monday,
                                moment(monday).endOf("isoWeek")
                              )
                            : formatDateRange(customStartDate, customEndDate)}
                        </p>
                      </div>
                      {group?.locations.map((location, locationIndex) => (
                        <LocationTable
                          key={locationIndex}
                          location={location}
                        />
                      ))}
                    </div>
                  ))
                : "No data available"}
            </div>
          )}
        </CardBody>
      </Card>
      {informationModal.isOpen ? (
        <InformationModal
          title={informationModal.title}
          body={informationModal.body}
          onClose={() =>
            setInformationModal({ isOpen: false, title: "", body: "" })
          }
        />
      ) : null}
    </Container>
  );
};

export default RecoveryReport;
